import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useGetAllInvoiceQuery,
  useGetCompanyQuery,
  useGetCustomerQuery,
  useGetWorkOrderQuery,
} from "../../../Api/attoDeskApi";
import { IInvoice } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { Link, useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";

import { CiSearch } from "react-icons/ci";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import axios from "axios";
import ViewWorkOrder from "../../CustomerTransaction/Components/ViewWorkOrder";
import Pagination from "../../../Components/Pagination";
import dayjs from "dayjs";
import { WorkOrderStatus } from "../../../Core/Enum/enum";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v: any) => `$${(parseFloat(String(v || 0)) || 0).toFixed(2)}`;
const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const deriveStatus = (grandTotal: number, amountPaid: number) => {
  if (amountPaid >= grandTotal && grandTotal > 0) {
    return { label: "Paid", color: "#16a34a", bg: "#dcfce7", key: "paid" };
  }
  return { label: "Pending", color: "#d97706", bg: "#fef3c7", key: "pending" };
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({
  icon, label, value, color, bg,
}: { icon: React.ReactNode; label: string; value: string; color: string; bg: string }) => (
  <Box
    sx={{
      flex: 1, minWidth: 140,
      bgcolor: "#fff", borderRadius: 3,
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      p: 2, display: "flex", alignItems: "center", gap: 1.5,
    }}
  >
    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Box sx={{ color, fontSize: 20, lineHeight: 0 }}>{icon}</Box>
    </Box>
    <Box>
      <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{value}</Typography>
    </Box>
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage } = useNotifier();

  const componentRef = useRef<HTMLDivElement>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  // Print an individual invoice using the SAME blade-rendered PDF as the
  // saved invoice file / email attachment (resources/views/pdf/invoice.blade.php).
  // No separate React layout — single source of truth.
  const handlePrintInvoice = async (invoiceId: number | string, template?: string) => {
  try {
    const response = await axios.get(`${API_URL}/invoices/${invoiceId}/pdf-saved`, {
      responseType: "blob",
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
    const printWindow = window.open(url, "_blank");
    if (printWindow) {
      printWindow.addEventListener("load", () => printWindow.print(), { once: true });
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch {
    showErrorMessage("Failed to load invoice PDF for printing.");
  }
};

  const [searchQuery, setSearchQuery]         = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage]                       = useState(0);
  const [rowsPerPage, setRowsPerPage]         = useState(50);
  const [selectedStatus, setSelectedStatus]   = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [dateFilter, setDateFilter]           = useState("");
  const [startDate, setStartDate]             = useState("");
  const [endDate, setEndDate]                 = useState("");
  const [openDelete, setOpenDelete]           = useState(false);
  const [toDelete, setToDelete]               = useState<IInvoice | null>(null);

  // Last-seen customer (persisted in localStorage for cross-session quick nav)
  const [lastSeenCustomer, setLastSeenCustomer] = useState<{ id: string; name: string } | null>(() => {
    try { return JSON.parse(localStorage.getItem("invoice_lastSeenCustomer") || "null"); } catch { return null; }
  });

  const trackCustomer = (id: string, name: string) => {
    const val = { id, name };
    setLastSeenCustomer(val);
    localStorage.setItem("invoice_lastSeenCustomer", JSON.stringify(val));
  };

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data, isLoading, isError, refetch } = useGetAllInvoiceQuery({
    searchText: currentSearchQuery, id: "", quoteWorkOrderCustomerId: "", quoteWorkOrderCustomerProjectId: "",
  });
  // Also fetch work orders so quote-converted invoices appear in the list
  const { data: workOrderData, isLoading: workOrderLoading, refetch: workOrderRefetch } = useGetWorkOrderQuery({ searchText: "", id: "" });
  const { data: companyData }  = useGetCompanyQuery({ searchText: "", id: "" });
  const { data: customerData } = useGetCustomerQuery({ searchText: "", id: "" });

  // Always fetch fresh data on mount so newly converted invoices appear immediately
  useEffect(() => {
    refetch();
    workOrderRefetch();
  }, []);

  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === "") {
      setCurrentSearchQuery(debouncedSearch);
      refetch();
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (dateFilter === "custom") return;
    let start = "", end = dayjs().format("YYYY-MM-DD");
    switch (dateFilter) {
      case "today":      start = end; break;
      case "yesterday":  start = end = dayjs().subtract(1,"day").format("YYYY-MM-DD"); break;
      case "this_week":  start = dayjs().startOf("week").format("YYYY-MM-DD"); break;
      case "last_week":
        start = dayjs().subtract(1,"week").startOf("week").format("YYYY-MM-DD");
        end   = dayjs().subtract(1,"week").endOf("week").format("YYYY-MM-DD");
        break;
      case "this_month": start = dayjs().startOf("month").format("YYYY-MM-DD"); break;
      case "last_month":
        start = dayjs().subtract(1,"month").startOf("month").format("YYYY-MM-DD");
        end   = dayjs().subtract(1,"month").endOf("month").format("YYYY-MM-DD");
        break;
      default: break;
    }
    setStartDate(start); setEndDate(end);
  }, [dateFilter]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchQuery.trim())  params.search     = searchQuery.trim();
      if (startDate)           params.start_date = startDate;
      if (endDate)             params.end_date   = endDate;
      if (selectedCompany)     params.company_id = selectedCompany;
      if (selectedCustomer)    params.customer_id = selectedCustomer;
      if (selectedStatus)      params.status     = selectedStatus;
      const response = await axios.get(`${API_URL}/pdf/invoices-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoices-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      try {
        const blob: Blob = err?.response?.data;
        const text = blob ? await blob.text() : "";
        const parsed = JSON.parse(text || "{}");
        showErrorMessage(parsed?.message || "Failed to generate invoice PDF report.");
      } catch {
        showErrorMessage("Failed to generate invoice PDF report.");
      }
    } finally { setPdfLoading(false); }
  };

  const handleExportToExcel = () => {
    const data = filtered.map((row, i) => {
      const amount  = parseFloat(String(row.quoteWorkOrderSaleGrandTotal || 0)) || 0;
      const paid    = parseFloat(String(row.amount_paid || 0)) || 0;
      const balance = Math.max(0, amount - paid);
      return {
        "#":           i + 1,
        "Invoice No":  row.InvoiceNo || "",
        "Quote No":    row.quoteWorkOrderNo || "",
        "PO Number":   row.po_number || "",
        "Status":      paid >= amount && amount > 0 ? "Paid" : "Pending",
        "Amount":      amount.toFixed(2),
        "Paid":        paid.toFixed(2),
        "Balance":     balance.toFixed(2),
        "Issued By":   row.company?.companyName || "",
        "Client":      row.customer ? `${row.customer.customerFirstName || ""} ${row.customer.customerLastName || ""}`.trim() || row.customer.customerCompanyName || "" : "",
        "Issue Date":  row.quoteWorkOrderIssueDate ? dayjs(row.quoteWorkOrderIssueDate).format("DD MMM YYYY") : "",
        "Due Date":    row.quoteWorkOrderDueDate   ? dayjs(row.quoteWorkOrderDueDate).format("DD MMM YYYY")   : "",
        "Trip ID":     (() => { const raw = row.quoteTripId; const prefix = (row.company as any)?.companyTripPrefix || ""; return raw ? (prefix ? `${prefix}${String(raw).padStart(5, "0")}` : String(raw)) : ""; })(),
        "Trip Name":   row.quoteTripName || "",
        "Trip Date":   (() => { const saleItems: any[] = Array.isArray(row.quoteWorkOrderSale) ? row.quoteWorkOrderSale : []; const dt = saleItems[0]?.departureDateTime; return dt && dayjs(dt).isValid() ? dayjs(dt).format("DD MMM YYYY") : ""; })(),
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    XLSX.writeFile(wb, `invoices_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  const coursings: IInvoice[] = useMemo(() => {
    const directInvoices: IInvoice[] = Array.isArray(data?.data) ? (data!.data as IInvoice[]) : [];
    const directIds = new Set(directInvoices.map((inv) => String(inv.work_order_id)));

    // Map quote-converted work orders to IInvoice shape for display
    const convertedFromQuotes: IInvoice[] = Array.isArray(workOrderData?.data)
      ? (workOrderData!.data as any[])
          .filter((wo) => (wo.isInvoice === true || wo.isInvoice == 1) && !directIds.has(String(wo.id)))
          .map((wo) => ({
            id: wo.invoiceId || wo.id,
            work_order_id: wo.id,
            InvoiceNo: String(wo.InvoiceNo || ""),
            po_number: wo.po_number || "",
            payment_id: wo.payment_id || "",
            quoteWorkOrderCompanyId: wo.quoteWorkOrderCompanyId,
            quoteWorkOrderCustomerId: wo.quoteWorkOrderCustomerId,
            quoteWorkOrderNo: wo.quoteWorkOrderNo,
            quoteWorkOrderIssueDate: wo.quoteWorkOrderIssueDate,
            quoteWorkOrderDueDate: wo.quoteWorkOrderDueDate,
            quoteWorkOrderAmount: wo.quoteWorkOrderAmount,
            quoteWorkOrderStatus: wo.quoteWorkOrderStatus,
            quoteWorkOrderSale: wo.quoteWorkOrderSale || [],
            quoteWorkOrderDiscount: wo.quoteWorkOrderDiscount || "",
            quoteWorkOrderSubTotal: wo.quoteWorkOrderSubTotal || "",
            quoteWorkOrderSaleGrandTotal: wo.quoteWorkOrderSaleGrandTotal || "0",
            quoteWorkOrderSaleDiscountDescription: null,
            customer: wo.customer,
            company: wo.company,
            project: wo.project || null,
            drivers: wo.assign_driver || null,
            created_at: wo.created_at,
            due_amount: wo.due_amount ?? null,
            amount: parseFloat(wo.quoteWorkOrderSaleGrandTotal || "0"),
            amount_paid: wo.amount_paid ?? null,
            assign_driver: wo.assign_driver,
            quoteTripId: wo.quoteTripId || "",
            quoteTripName: wo.quoteTripName || "",
          } as IInvoice))
      : [];

    return [...directInvoices, ...convertedFromQuotes];
  }, [data, workOrderData]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return coursings.filter((row) => {
      const matchesDate = !dateFilter || !startDate || (() => {
        const d = new Date(row.created_at).toLocaleDateString("en-CA");
        const s = new Date(startDate).toLocaleDateString("en-CA");
        const e = endDate ? new Date(endDate).toLocaleDateString("en-CA") : s;
        return d >= s && d <= e;
      })();
      const matchesCompany   = !selectedCompany  || String(row.company?.id)  === String(selectedCompany);
      const matchesCustomer  = !selectedCustomer || String(row.customer?.id) === String(selectedCustomer);
      const rowAmount  = parseFloat(String(row.quoteWorkOrderSaleGrandTotal || 0)) || 0;
      const rowPaid    = parseFloat(String(row.amount_paid || 0)) || 0;
      const rowStatus  = deriveStatus(rowAmount, rowPaid);
      const matchesStatus    = !selectedStatus   || rowStatus.key === selectedStatus;
      const matchesSearch    = !q || [
        row.InvoiceNo,
        row.quoteWorkOrderNo,
        row.po_number,
        row.customer?.customerFirstName,
        row.customer?.customerLastName,
        `${row.customer?.customerFirstName || ""} ${row.customer?.customerLastName || ""}`,
        row.customer?.customerCompanyName,
        row.company?.companyName,
      ].some((v) => String(v || "").toLowerCase().includes(q));
      if (row.quoteWorkOrderStatus === WorkOrderStatus.Cancel) return false;
      return matchesDate && matchesCompany && matchesCustomer && matchesStatus && matchesSearch;
    });
  }, [coursings, searchQuery, dateFilter, startDate, endDate, selectedCompany, selectedCustomer, selectedStatus]);

  // ── Summary totals ──────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let amount = 0, paid = 0;
    filtered.forEach((r) => {
      amount += parseFloat(String(r.quoteWorkOrderSaleGrandTotal || 0)) || 0;
      paid   += parseFloat(String(r.amount_paid || 0)) || 0;
    });
    return { amount, paid, balance: Math.max(0, amount - paid), count: filtered.length };
  }, [filtered]);

  // ── Table helpers ───────────────────────────────────────────────────────────
  const thSx = {
    fontWeight: 700, fontSize: 11, color: "#64748b",
    textTransform: "uppercase" as const, letterSpacing: "0.06em",
    py: 1.5, px: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc",
  };
  const tdSx = { py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9", fontSize: 13, color: "#1e293b" };
  const filterSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2, fontSize: 13, bgcolor: "#fff", height: 36,
      "&:hover fieldset": { borderColor: "#2f3891" },
      "&.Mui-focused fieldset": { borderColor: "#2f3891" },
    },
  };

  const paged = rowsPerPage === 0 ? filtered : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: "linear-gradient(135deg,#0ea5e9,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ReceiptLongOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>Invoices</Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>{totals.count} invoice{totals.count !== 1 ? "s" : ""}</Typography>
        </Box>

        {/* Last-seen customer quick nav */}
        {lastSeenCustomer && (
          <Box
            onClick={() => navigate(`/customer-history/${lastSeenCustomer.id}`)}
            sx={{
              display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.7,
              borderRadius: 2, border: "1px solid #e2e8f0", bgcolor: "#fff",
              cursor: "pointer", "&:hover": { bgcolor: "#f8fafc", borderColor: "#2f3891" },
              transition: "all 0.15s",
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 15, color: "#2f3891" }} />
            <Box>
              <Typography sx={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1 }}>Last seen</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", lineHeight: 1.3 }}>{lastSeenCustomer.name}</Typography>
            </Box>
          </Box>
        )}

        <Button
          variant="contained"
          size="small"
          startIcon={<AddCircleOutlineIcon sx={{ fontSize: 15 }} />}
          onClick={() => navigate("/quote/create?asInvoice=true")}
          sx={{
            background: "linear-gradient(135deg,#0ea5e9,#2563eb)",
            textTransform: "none", fontWeight: 700, fontSize: 12,
            borderRadius: 2, px: 2, py: 0.9,
            boxShadow: "0 2px 8px rgba(14,165,233,0.35)",
            "&:hover": { background: "linear-gradient(135deg,#0284c7,#1d4ed8)" },
          }}
        >
          New Invoice
        </Button>
      </Box>

      {/* ── Stat cards ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard icon={<ReceiptLongOutlinedIcon fontSize="inherit" />}        label="Total Invoices" value={String(totals.count)}       color="#2563eb" bg="#dbeafe" />
        <StatCard icon={<AttachMoneyIcon fontSize="inherit" />}                label="Total Amount"   value={fmt(totals.amount)}         color="#2f3891" bg="#ede9fe" />
        <StatCard icon={<AccountBalanceWalletOutlinedIcon fontSize="inherit"/>} label="Collected"      value={fmt(totals.paid)}           color="#16a34a" bg="#dcfce7" />
        <StatCard icon={<PendingActionsOutlinedIcon fontSize="inherit" />}     label="Outstanding"    value={fmt(totals.balance)}        color="#dc2626" bg="#fee2e2" />
      </Box>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)", p: 2, mb: 2,
          display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end",
        }}
      >
        {/* Search */}
        <TextField
          size="small" placeholder="Search invoice, quote, client…"
          value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ color: "#94a3b8", fontSize: 18 }} /></InputAdornment>, sx: { borderRadius: 2, fontSize: 13, bgcolor: "#fff", height: 36, minWidth: 200 } }}
          sx={filterSx}
        />

        {/* Status */}
        <Select size="small" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} displayEmpty sx={{ ...filterSx["& .MuiOutlinedInput-root"], minWidth: 130 }}>
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
        </Select>

        {/* Company */}
        <Select size="small" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} displayEmpty sx={{ ...filterSx["& .MuiOutlinedInput-root"], minWidth: 150 }}>
          <MenuItem value="">All Companies</MenuItem>
          {Array.isArray(companyData?.data) && companyData?.data.slice().sort((a: any, b: any) => (a.companyName || "").localeCompare(b.companyName || "")).map((c: any) => (
            <MenuItem key={c.id} value={c.id}>{cap(c.companyName)}</MenuItem>
          ))}
        </Select>

        {/* Customer */}
        <Select size="small" value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)} displayEmpty sx={{ ...filterSx["& .MuiOutlinedInput-root"], minWidth: 160 }}>
          <MenuItem value="">All Customers</MenuItem>
          {Array.isArray(customerData?.data) && customerData?.data.slice().sort((a: any, b: any) => {
            const nameA = `${a.customerFirstName || ""} ${a.customerLastName || ""}`.trim().toLowerCase();
            const nameB = `${b.customerFirstName || ""} ${b.customerLastName || ""}`.trim().toLowerCase();
            return nameA.localeCompare(nameB);
          }).map((c: any) => (
            <MenuItem key={c.id} value={c.id}>{cap(c.customerFirstName)} {cap(c.customerLastName)}</MenuItem>
          ))}
        </Select>

        {/* Date preset */}
        <Select size="small" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} displayEmpty sx={{ ...filterSx["& .MuiOutlinedInput-root"], minWidth: 140 }}>
          <MenuItem value="">All Dates</MenuItem>
          <MenuItem value="today">Today</MenuItem>
          <MenuItem value="yesterday">Yesterday</MenuItem>
          <MenuItem value="this_week">This Week</MenuItem>
          <MenuItem value="last_week">Last Week</MenuItem>
          <MenuItem value="this_month">This Month</MenuItem>
          <MenuItem value="last_month">Last Month</MenuItem>
          <MenuItem value="custom">Custom Range</MenuItem>
        </Select>

        {dateFilter === "custom" && (
          <>
            <TextField size="small" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={filterSx} InputProps={{ sx: { borderRadius: 2, height: 36, fontSize: 13 } }} />
            <TextField size="small" type="date" value={endDate}   onChange={(e) => setEndDate(e.target.value)}   sx={filterSx} InputProps={{ sx: { borderRadius: 2, height: 36, fontSize: 13 } }} />
          </>
        )}

        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          {(selectedStatus || selectedCompany || selectedCustomer || dateFilter || searchQuery) && (
            <Button size="small" onClick={() => { setSelectedStatus(""); setSelectedCompany(""); setSelectedCustomer(""); setDateFilter(""); setSearchQuery(""); setCurrentSearchQuery(""); }}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", borderColor: "#e2e8f0", border: "1px solid" }}>
              Clear filters
            </Button>
          )}
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{
              height: 36, fontSize: 12, borderRadius: 2,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            }}
          >
            <MenuItem value={50}>50 / page</MenuItem>
            <MenuItem value={100}>100 / page</MenuItem>
            <MenuItem value={0}>Show All</MenuItem>
          </Select>
          <Button size="small" variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <LocalPrintshopSharpIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownloadPdf} disabled={pdfLoading}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", borderColor: "#e2e8f0" }}>
            {pdfLoading ? "Generating…" : "Print"}
          </Button>
          {filtered.length > 0 && (
            <Button size="small" variant="outlined"
              startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={handleExportToExcel}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#16a34a", borderColor: "#e2e8f0" }}>
              Excel
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <Box ref={componentRef} sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        {workOrderLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#2563eb" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>#</TableCell>
                  <TableCell sx={thSx}>Invoice No.</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right" }}>Amount</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right" }}>Paid</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right" }}>Balance</TableCell>
                  <TableCell sx={thSx}>Issued By</TableCell>
                  <TableCell sx={thSx}>Client</TableCell>
                  <TableCell sx={thSx}>Trip Details</TableCell>
                  <TableCell sx={thSx}>Trip Date</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right", "@media print": { display: "none" } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <ReceiptLongOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                        <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
                          {coursings.length === 0 ? "No converted invoices yet. Convert a quote to invoice to see it here." : "No invoices match your filters"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((row: IInvoice, index) => {
                    const amount  = parseFloat(String(row.quoteWorkOrderSaleGrandTotal || 0)) || 0;
                    const paid    = parseFloat(String(row.amount_paid || 0)) || 0;
                    const st      = deriveStatus(amount, paid);
                    const balance = Math.max(0, amount - paid);
                    const isPaid  = balance === 0 && paid > 0;

                    return (
                      <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fafcff" }, "&:last-child td": { borderBottom: 0 } }}>
                        {/* # */}
                        <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 48 }}>{page * rowsPerPage + index + 1}</TableCell>

                        {/* Invoice number */}
                        <TableCell sx={tdSx}>
                          <Link to={`/work-order/history/${row.work_order_id}`} style={{ textDecoration: "none" }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#2563eb", "&:hover": { textDecoration: "underline" } }}>
                              {row.InvoiceNo || `INV-${row.id}`}
                            </Typography>
                          </Link>
                          {row.po_number && (
                            <Typography sx={{ fontSize: 10, color: "#64748b", mt: 0.2 }}>PO: {row.po_number}</Typography>
                          )}
                        </TableCell>

                        {/* Status chip */}
                        <TableCell sx={tdSx}>
                          <Chip label={st.label} size="small"
                            sx={{ height: 22, fontSize: 11, fontWeight: 700, color: st.color, bgcolor: st.bg, border: `1px solid ${st.color}33` }} />
                        </TableCell>

                        {/* Amount */}
                        <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 600 }}>{fmt(amount)}</TableCell>

                        {/* Paid */}
                        <TableCell sx={{ ...tdSx, textAlign: "right", color: paid > 0 ? "#16a34a" : "#94a3b8", fontWeight: paid > 0 ? 600 : 400 }}>
                          {fmt(paid)}
                        </TableCell>

                        {/* Balance */}
                        <TableCell sx={{ ...tdSx, textAlign: "right", color: isPaid ? "#16a34a" : balance > 0 ? "#dc2626" : "#94a3b8", fontWeight: 600 }}>
                          {fmt(balance)}
                        </TableCell>

                        {/* Issued by */}
                        <TableCell sx={tdSx}>
                          {row.company?.companyName ? (() => {
                            const name: string = row.company.companyName;
                            const isBusLines = name.toLowerCase().includes("bus line");
                            return (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  px: 1.25,
                                  py: 0.4,
                                  borderRadius: "6px",
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  letterSpacing: "0.02em",
                                  whiteSpace: "nowrap",
                                  bgcolor: isBusLines ? "#dbeafe" : "#dcfce7",
                                  color: isBusLines ? "#1e40af" : "#15803d",
                                }}
                              >
                                {name}
                              </Box>
                            );
                          })() : (row.project?.projectName || "—")}
                        </TableCell>

                        {/* Client */}
                        <TableCell sx={tdSx}>
                          {(() => {
                            const first = cap(row.customer?.customerFirstName);
                            const last  = cap(row.customer?.customerLastName);
                            const company = row.customer?.customerCompanyName;
                            const fullName = [first, last].filter(Boolean).join(" ");
                            const displayName = fullName || company || "—";
                            return (
                              <Link
                                to={`/customer-history/${row.quoteWorkOrderCustomerId}`}
                                style={{ textDecoration: "none" }}
                                onClick={() => {
                                  if (fullName) trackCustomer(String(row.quoteWorkOrderCustomerId), fullName);
                                }}
                              >
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", "&:hover": { color: "#2563eb" } }}>
                                  {first || last || displayName}
                                </Typography>
                                {first && last && (
                                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                    {last}
                                  </Typography>
                                )}
                              </Link>
                            );
                          })()}
                        </TableCell>

                        {/* Trip Details */}
                        <TableCell sx={{ ...tdSx, maxWidth: 260 }}>
                          {(() => {
                            const sales: any[] = Array.isArray(row.quoteWorkOrderSale) ? row.quoteWorkOrderSale : [];
                            if (!sales.length) return <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>—</Typography>;
                            return (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                                {sales.slice(0, 2).map((sale: any, si: number) => {
                                  const firstPickup = Array.isArray(sale.pickups) && sale.pickups.length > 0 ? sale.pickups[0] : null;
                                  const from = (sale.transferTypeFrom || firstPickup?.city || "").trim();
                                  const to   = (sale.transferTypeTo   || "").trim();
                                  const busType = sale.productShortDescription || sale.productName || "";
                                  const isRound = String(sale.transferType) === "1";
                                  return (
                                    <Box key={si} sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                                      {(from || to) && (
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                          {from}{from && to ? " → " : ""}{to}
                                        </Typography>
                                      )}
                                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                        {busType && (
                                          <Box sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#475569", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "4px", px: 0.7, py: "1px", whiteSpace: "nowrap" }}>
                                            {busType}
                                          </Box>
                                        )}
                                        <Box sx={{ fontSize: "0.68rem", fontWeight: 600, color: isRound ? "#7c3aed" : "#0369a1", bgcolor: isRound ? "#ede9fe" : "#e0f2fe", border: `1px solid ${isRound ? "#ddd6fe" : "#bae6fd"}`, borderRadius: "4px", px: 0.7, py: "1px", whiteSpace: "nowrap" }}>
                                          {isRound ? "Round Trip" : "One Way"}
                                        </Box>
                                      </Box>
                                    </Box>
                                  );
                                })}
                                {sales.length > 2 && (
                                  <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8" }}>+{sales.length - 2} more leg{sales.length - 2 > 1 ? "s" : ""}</Typography>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>

                        {/* Trip Date */}
                        <TableCell sx={tdSx}>
                          {(() => {
                            const sales: any[] = Array.isArray(row.quoteWorkOrderSale) ? row.quoteWorkOrderSale : [];
                            const firstPickup = Array.isArray(sales[0]?.pickups) && sales[0].pickups.length > 0 ? sales[0].pickups[0] : null;
                            const dt = firstPickup?.dateTime || sales[0]?.departureDateTime;
                            return dt && dayjs(dt).isValid()
                              ? <Typography sx={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 500, whiteSpace: "nowrap" }}>{dayjs(dt).format("MMM D, YYYY · h:mm A")}</Typography>
                              : <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>—</Typography>;
                          })()}
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ ...tdSx, textAlign: "right", "@media print": { display: "none" } }}>
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <Tooltip title="Print invoice" arrow>
                              <IconButton size="small" onClick={() => handlePrintInvoice(row.id, (row.company as any)?.company_document_template)}
                                sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f0f9ff", color: "#0ea5e9", "&:hover": { bgcolor: "#e0f2fe" } }}>
                                <LocalPrintshopSharpIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View quote" arrow>
                              <IconButton size="small" onClick={() => navigate(`/work-order/history/${row.work_order_id}`)}
                                sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#f8fafc", color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}>
                                <VisibilitySharpIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit invoice" arrow>
                              <IconButton size="small" onClick={() => navigate(row.work_order_id ? `/quote-history/${row.work_order_id}` : `/invoice/edit/${row.id}`)}
                                sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: "#eef2ff", color: "#2f3891", "&:hover": { bgcolor: "#e0e7ff" } }}>
                                <EditSharpIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}

                {/* Totals row */}
                {filtered.length > 0 && (
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell colSpan={3} sx={{ ...tdSx, fontWeight: 700, color: "#475569", borderTop: "2px solid #e2e8f0" }}>
                      Totals ({filtered.length} invoices)
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 700, color: "#0f172a", borderTop: "2px solid #e2e8f0" }}>{fmt(totals.amount)}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 700, color: "#16a34a", borderTop: "2px solid #e2e8f0" }}>{fmt(totals.paid)}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 700, color: totals.balance > 0 ? "#dc2626" : "#16a34a", borderTop: "2px solid #e2e8f0" }}>{fmt(totals.balance)}</TableCell>
                    <TableCell colSpan={5} sx={{ ...tdSx, borderTop: "2px solid #e2e8f0" }} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {filtered.length > 0 && rowsPerPage > 0 && (
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
            <Pagination count={filtered.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </Box>

      {/* Print uses the saved invoice PDF directly — no preview dialog. */}

      {/* Delete dialog */}
      {toDelete && (
        <DeletePopup
          open={openDelete}
          handleCloseDelete={() => { setToDelete(null); setOpenDelete(false); }}
          onConfirm={async () => { setOpenDelete(false); }}
          title="Delete Invoice"
          content={`Are you sure you want to delete invoice "${toDelete.InvoiceNo}"?`}
        />
      )}
    </Box>
  );
};

export default Index;
